import React, { useRef, useState } from 'react';
import { Share2, Link, Check, Twitter, Hash } from 'lucide-react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import { PhoneModel } from '@smartphone-tycoon/shared-3d';

interface ShareButtonProps {
  name: string;
  price: number;
  specs: {
    cpu: string;
    gpu: string;
    ram: string;
    storage: string;
    network: string;
    os: string;
  };
  previewConfig: {
    color: string;
    material: string;
    buttonColor: string;
    hasButtons: boolean;
    screenBorderColor: string;
    cameraStyle: string;
    cameraColor: string;
    cameraPosition: { x: number; y: number };
    buttonPositions: { volume: number; power: number };
    phoneSize: { width: number; height: number; depth: number };
    screenSize: { width: number; height: number };
    cornerRadius: number;
    logo: {
      type: string;
      url?: string;
      color?: string;
      position: { x: number; y: number };
      rotation: number;
      scale: number;
    };
  };
  shareableUrl: string;
}

export function ShareButton({ name, price, specs, previewConfig, shareableUrl }: ShareButtonProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [showSocialOptions, setShowSocialOptions] = useState(false);

  const capturePreview = async (): Promise<Blob> => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      canvas.width = 600;
      canvas.height = 600;
      const ctx = canvas.getContext('2d')!;
      ctx.fillStyle = '#f3f4f6';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const phoneCanvas = containerRef.current?.querySelector('canvas');
      if (phoneCanvas) {
        ctx.drawImage(phoneCanvas, 0, 0, canvas.width, canvas.height);
      }

      canvas.toBlob((blob) => resolve(blob!), 'image/png');
    });
  };

  const generateShareText = () => {
    return `Check out my custom ${name} phone design!\n` +
      `Price: $${price}\n` +
      `Specs: ${specs.cpu}, ${specs.gpu}, ${specs.ram} RAM, ${specs.storage} Storage, ${specs.network}\n` +
      `OS: ${specs.os}\n` +
      `Configure it yourself: ${shareableUrl}\n` +
      `#CustomPhone #TechDesign`;
  };

  const handleShare = async () => {
    try {
      setSharing(true);
      const imageBlob = await capturePreview();
      const imageFile = new File([imageBlob], 'phone-design.png', { type: 'image/png' });
      const text = generateShareText();

      if (navigator.share && navigator.canShare({ files: [imageFile] })) {
        await navigator.share({
          text,
          files: [imageFile],
        });
      } else {
        setShowSocialOptions(true);
      }
    } catch (error) {
      console.error('Error sharing:', error);
    } finally {
      setSharing(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareableUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Error copying link:', error);
    }
  };

  const shareToX = async () => {
    const text = generateShareText();
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`, '_blank');
  };

  const shareToThreads = async () => {
    const text = generateShareText();
    window.open(`https://www.threads.net/create?text=${encodeURIComponent(text)}`, '_blank');
  };

  return (
    <div className="relative">
      <div className="flex gap-2">
        <button
          onClick={handleCopyLink}
          className="flex items-center gap-2 px-4 py-2 bg-theme-bg-content rounded-theme shadow-theme hover:bg-theme-bg-secondary transition-colors"
          aria-label="Copy share link"
        >
          {copied ? (
            <Check className="w-4 h-4 text-theme-success" />
          ) : (
            <Link className="w-4 h-4 text-theme-accent" />
          )}
          <span className="text-sm text-theme-text-secondary">{copied ? 'Copied!' : 'Copy Link'}</span>
        </button>

        <button
          onClick={handleShare}
          disabled={sharing}
          className="flex items-center gap-2 px-4 py-2 bg-theme-bg-content rounded-theme shadow-theme hover:bg-theme-bg-secondary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Share design"
        >
          <Share2 className="w-4 h-4 text-theme-accent" />
          <span className="text-sm text-theme-text-secondary">{sharing ? 'Sharing...' : 'Share Design'}</span>
        </button>

        <div
          ref={containerRef}
          className="fixed right-[-9999px] bottom-[-9999px] w-[600px] h-[600px] pointer-events-none"
          aria-hidden="true"
        >
          <Canvas shadows camera={{ position: [0, 0, 10], fov: 45 }}>
            <ambientLight intensity={0.5} />
            <spotLight position={[10, 10, 10]} angle={0.15} penumbra={1} intensity={1} castShadow />
            <PhoneModel {...previewConfig} />
            <OrbitControls autoRotate autoRotateSpeed={2} enableZoom={false} enablePan={false} />
          </Canvas>
        </div>
      </div>

      {showSocialOptions && (
        <div className="absolute top-full mt-2 right-0 bg-theme-bg-content rounded-theme shadow-theme p-2 z-50">
          <div className="flex flex-col gap-2">
            <button
              onClick={shareToX}
              className="flex items-center gap-2 px-4 py-2 hover:bg-theme-bg-secondary rounded-theme transition-colors"
            >
              <Twitter className="w-4 h-4" />
              <span className="text-sm text-theme-text-secondary">Share to X</span>
            </button>
            <button
              onClick={shareToThreads}
              className="flex items-center gap-2 px-4 py-2 hover:bg-theme-bg-secondary rounded-theme transition-colors"
            >
              <Hash className="w-4 h-4" />
              <span className="text-sm text-theme-text-secondary">Share to Threads</span>
            </button>
            <button
              onClick={() => setShowSocialOptions(false)}
              className="text-sm text-theme-text-muted hover:text-theme-text-secondary px-4 py-1"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}